import React, { useState, useEffect } from 'react';
import { storage } from './firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

const API_URL = `${process.env.REACT_APP_API_URL}/api/hiring-documents`;

function AdminHiringDocuments() {
  const [currentDocument, setCurrentDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchDocument();
  }, []);

  const fetchDocument = async () => {
    try {
      const res = await fetch(API_URL);
      const data = await res.json();
      if (res.ok && data.data) {
        setCurrentDocument(data.data);
      }
    } catch (err) {
      console.error('Failed to load document');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      setMessage('❌ Only PDF, DOC, and DOCX files are allowed');
      return;
    }

    setUploading(true);
    setMessage('');

    try {
      const fileRef = ref(storage, `hiring-documents/${Date.now()}_${file.name}`);
      await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(fileRef);

      const payload = {
        fileName: file.name,
        fileURL: downloadURL,
        fileType: file.type
      };

      await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      setMessage('✅ Document uploaded successfully!');
      fetchDocument();
    } catch (err) {
      setMessage('❌ Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this document?')) return;

    try {
      if (currentDocument.fileURL) {
        await deleteObject(ref(storage, currentDocument.fileURL));
      }

      await fetch(API_URL, { method: 'DELETE' });
      setCurrentDocument(null);
      setMessage('✅ Document deleted successfully');
    } catch (err) {
      setMessage('❌ Failed to delete document');
    }
  };

  const getFileIcon = (fileName) => {
    const ext = fileName?.split('.').pop()?.toLowerCase();
    return ext === 'pdf' ? '📕' : '📘';
  };

  if (loading) {
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>;
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa', padding: '2rem' }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <div style={{ marginBottom: '3rem', textAlign: 'center' }}>
          <h1 style={{ fontSize: '2.5rem', fontWeight: '300', color: '#222', marginBottom: '0.5rem' }}>
            Hiring <span style={{ fontWeight: '600', color: '#2c5282' }}>Agreement</span>
          </h1>
          <p style={{ color: '#666', fontSize: '1rem' }}>
            Upload and manage the hiring agreement document
          </p>
        </div>

        {message && (
          <div style={{
            padding: '1rem',
            marginBottom: '2rem',
            borderRadius: '8px',
            background: message.startsWith('✅') ? '#d4edda' : '#f8d7da',
            color: message.startsWith('✅') ? '#155724' : '#721c24',
            textAlign: 'center'
          }}>
            {message}
          </div>
        )}

        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '2rem',
          boxShadow: '0 8px 25px rgba(0,0,0,0.08)',
          marginBottom: '2rem'
        }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1.5rem', color: '#333' }}>
            📄 Current Agreement
          </h2>

          {currentDocument?.fileURL ? (
            <div>
              <div style={{
                padding: '1.5rem',
                background: '#f8f9fa',
                borderRadius: '12px',
                border: '1px solid #e0e0e0',
                marginBottom: '1.5rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ fontSize: '2.5rem' }}>{getFileIcon(currentDocument.fileName)}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '1.1rem', fontWeight: '600', color: '#333' }}>
                      {currentDocument.fileName}
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#666' }}>
                      Uploaded on {new Date(currentDocument.updatedAt || currentDocument.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <a
                  href={currentDocument.fileURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    flex: 1,
                    padding: '1rem',
                    background: '#2c5282',
                    color: 'white',
                    textAlign: 'center',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    fontWeight: '600'
                  }}
                >
                  View Document
                </a>
                <button
                  onClick={handleDelete}
                  style={{
                    flex: 1,
                    padding: '1rem',
                    background: '#e53e3e',
                    color: 'white',
                    borderRadius: '8px',
                    fontWeight: '600'
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📄</div>
              <p style={{ fontSize: '1.1rem' }}>No document uploaded yet</p>
            </div>
          )}
        </div>

        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '2rem',
          boxShadow: '0 8px 25px rgba(0,0,0,0.08)'
        }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: '#333' }}>
            📤 Upload Document
          </h2>
          <p style={{ color: '#666', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
            Only PDF, DOC, and DOCX files are supported.
          </p>

          <label style={{
            display: 'block',
            padding: '2rem',
            background: uploading ? '#f0f0f0' : '#f8f9fa',
            border: '2px dashed #2c5282',
            borderRadius: '12px',
            textAlign: 'center',
            cursor: uploading ? 'not-allowed' : 'pointer'
          }}>
            <input
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleFileUpload}
              disabled={uploading}
              style={{ display: 'none' }}
            />
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
              {uploading ? '⏳' : '📁'}
            </div>
            <div style={{ fontSize: '1.1rem', fontWeight: '600', color: '#2c5282' }}>
              {uploading ? 'Uploading...' : 'Click to upload document'}
            </div>
            <div style={{ fontSize: '0.9rem', color: '#666' }}>
              PDF, DOC, or DOCX
            </div>
          </label>
        </div>
      </div>
    </div>
  );
}

export default AdminHiringDocuments;